import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faUtensils,
  faBus,
  faBuilding
} from "@fortawesome/free-solid-svg-icons";

import ReusableGoogleMapWithPanel from "../../../components/ReusableGoogleMapWithPanel";

/* ===========================
   CATEGORY CONFIG
=========================== */

const CATEGORIES = [
  {
    key: "food",
    label: "Food & Drink",
    icon: faUtensils,
    color: "#f59e0b",
    sub: ["Cafe", "Restaurant", "Bar", "Fast Food"],
    marker: "/markers/mappin_orange.png",
	api:"/resolutionsoftwareplatform/api/local-info/places?type=RESTAURANTS&lat=51.5074&lng=-0.1278"
  },
  {
    key: "transport",
    label: "Transport",
    icon: faBus,
    color: "#2563eb",
    sub: ["Bus Stop", "Train", "Underground", "EV Charging Point"],
    marker: "/markers/bluepin.png",
	api:"/resolutionsoftwareplatform/api/local-info/places?type=EV_CHARGING&lat=51.5074&lng=-0.1278"
  },
  {
    key: "amenities",
    label: "Amenities",
    icon: faBuilding,
    color: "#16a34a",
    sub: ["Pharmacy", "Hospital", "ATM", "Gym"],
    marker: "/markers/yellowpin.png",
	api:""
  }
];

/* ===========================
   FAKE MARKERS
=========================== */

function generateMarkers(marker) {
  return Array.from({ length: 14 }).map((_, i) => ({
    id: i,
    top: 18 + Math.random() * 55,
    left: 12 + Math.random() * 72,
    marker
  }));
}

/* ===========================
   COMPONENT
=========================== */

export default function LocalInfoDrawer() {
  const [openCategory, setOpenCategory] = useState(null);
  const [activeSub, setActiveSub] = useState(null);
  
  const [apiUrl, setApiUrl] = useState("");
  const[markerUrl, setMarkerUrl] = useState("");

  const activeCategory = CATEGORIES.find(c => c.key === openCategory);

  const markers = useMemo(() => {
    if (!activeCategory || !activeSub) return [];
    return generateMarkers(activeCategory.marker);
  }, [activeCategory, activeSub]);

  return (
    <div style={layout}>

      {/* LEFT PANEL */}
      <div style={leftPanel}>
        {CATEGORIES.map(cat => {
          const open = openCategory === cat.key;

          return (
            <div key={cat.key} style={{ marginBottom: 14 }}>

              {/* CATEGORY HEADER */}
              <div
                onClick={() => {
                  setOpenCategory(open ? null : cat.key);
                  setActiveSub(null);
                }}
                style={{
                  ...categoryBox,
                  background: cat.color,
                  borderBottomLeftRadius: open ? 0 : 16,
                  borderBottomRightRadius: open ? 0 : 16
                }}
              >
                <div style={categoryLabel}>
                  <FontAwesomeIcon icon={cat.icon} />
                  {cat.label}
                </div>
                <FontAwesomeIcon
                  icon={open ? faChevronDown : faChevronRight}
                />
              </div>

              {/* SUB CATEGORIES */}
              <div
                style={{
                  ...subPanel,
                  maxHeight: open ? 220 : 0,
                  background: lighten(cat.color)
                }}
              >
                {cat.sub.map(sub => (
                  <div
                    key={sub}
                    onClick={() => {
						setActiveSub(sub);
						setMarkerUrl(cat.marker);
						setApiUrl(cat.api);
				    }}
                    style={{
                      ...subItem,
                      fontWeight: activeSub === sub ? 800 : 600
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                    {sub}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* MAP */}
      <div style={mapWrap}>
		  <ReusableGoogleMapWithPanel
		    apiUrl={apiUrl}
		    markerIcon={markerUrl}
		    center={{ lat: 51.5074, lng: -0.1278 }}
		    activeSub={activeSub}
		  />
      </div>
    </div>
  );
}

/* ===========================
   STYLES
=========================== */

const layout = { display: "flex", height: "100%" };

const leftPanel = { width: 200, padding: 16 };

const categoryBox = {
  color: "#fff",
  padding: "14px 16px",
  borderRadius: 16,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 800,
  transition: "all 0.3s ease"
};

const categoryLabel = {
  display: "flex",
  gap: 10,
  alignItems: "center"
};

const subPanel = {
  overflow: "hidden",
  transition: "max-height 0.6s ease",
  borderBottomLeftRadius: 16,
  borderBottomRightRadius: 16
};

const subItem = {
  padding: "10px 16px",
  display: "flex",
  gap: 10,
  alignItems: "center",
  cursor: "pointer",
  fontSize: 13
};

const mapWrap = {
  flex: 1,
  position: "relative",
  overflow: "hidden",
  borderRadius: 18
};

const map = { width: "100%", height: "100%", border: "none" };

const listPanel = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: 220,
  background: "#fff",
  transition: "transform 0.45s ease",
  borderTopLeftRadius: 22,
  borderTopRightRadius: 22,
  boxShadow: "0 -14px 40px rgba(0,0,0,0.25)",
  padding: 18
};

const listHeader = {
  fontWeight: 900,
  fontSize: 15,
  marginBottom: 12
};

const listGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  gap: 12
};

const listCard = {
  background: "#f5f7fb",
  borderRadius: 12,
  padding: 12
};

const listTitle = { fontWeight: 700, fontSize: 13 };
const listMeta = { fontSize: 11, opacity: 0.65 };

function lighten(hex) {
  return hex + "33";
}
